/**
 * @file BillQtySaveDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-16
 * @modified 2026-10-16
 *
 * Saving a bill whose quantities differ from what its POs say was supplied
 * (Cam, 16 Oct 2026): one plain window, a line per PO line, each with its
 * choice — set the PO's supplied qty to the bill's / leave it and mark the
 * bill Queried / leave it as it is (utils/billQtyOnSave). × and "Back to the
 * bill" go back to the form; "Save with these choices" saves.
 *
 * The shared ConfirmDialog with a table of Dropdowns, as CreditStockDialog.
 */

import React from 'react';
import ConfirmDialog from './ConfirmDialog';
import Dropdown from './Dropdown';
import './BillQtySaveDialog.css';

function BillQtySaveBody({ questions, answers, onChoose }) {
  return (
    <div className="bill-qty-save-body">
      <p className="bill-qty-save-intro">
        The bill&apos;s quantity is not what {questions.length === 1 ? 'its PO says' : 'their POs say'} was supplied.
        Choose for each line, then save.
      </p>
      <div className="table-container">
        <table className="data-table bill-qty-save-table">
          <thead>
            <tr>
              <th>PO line</th>
              <th>Quantities</th>
              <th>When the bill is saved</th>
            </tr>
          </thead>
          <tbody>
            {questions.map(q => (
              <tr key={q.key}>
                <td>
                  <div className="bill-qty-save-product">{q.poNumber}</div>
                  <div className="bill-qty-save-sub">{q.what}</div>
                </td>
                <td>
                  <div>{q.text}</div>
                  {q.warning && <div className="bill-qty-save-sub warn">{q.warning}</div>}
                </td>
                <td className="bill-qty-save-choice">
                  <Dropdown
                    value={answers[q.key] || q.initial}
                    escapeOverflow
                    onChange={(v) => onChoose(q.key, v)}
                    options={q.options.map(o => ({ value: o.value, label: o.label, disabled: !!o.disabled, title: o.title }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {object[]} props.questions — utils/billQtyOnSave.qtyQuestions
 * @param {object}   props.answers   — key → copy | query | leave
 * @param {Function} props.onChoose  (key, value)
 * @param {Function} props.onConfirm save with the choices
 * @param {Function} props.onCancel  back to the bill
 */
function BillQtySaveDialog({ isOpen, questions = [], answers = {}, onChoose, onConfirm, onCancel }) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      type="info"
      className="bill-qty-save-dialog"
      title="Bill quantities and the POs"
      message={<BillQtySaveBody questions={questions} answers={answers} onChoose={onChoose} />}
      confirmText="Save with these choices"
      confirmIcon="save"
      cancelText="Back to the bill"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

export default BillQtySaveDialog;
